import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, Check, LogOut, ChevronRight, Users, Dumbbell, Calendar } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../utils/api';
import { useTranslation } from 'react-i18next';

const CoachDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [notifCount, setNotifCount] = useState(0);

  useEffect(() => {
    fetchClients();
    apiFetch('/api/notifications/count')
      .then((r) => r.ok ? r.json() : { count: 0 })
      .then((d) => setNotifCount(d.count || 0))
      .catch(() => {});
  }, []);

  const fetchClients = async () => {
    try {
      const res = await apiFetch('/api/coach/clients');
      if (res.ok) {
        const data = await res.json();
        setClients(data.clients || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = () => {
    if (!user?.invite_code) return;
    navigator.clipboard.writeText(user.invite_code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading) return <div className="loading"><div className="loading-spinner" /></div>;

  return (
    <div className="page-container animate-fade-in">
      <div className="header">
        <div>
          <p className="text-muted" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            {t('coach_dashboard.panel_label')}
          </p>
          <h1 className="header-title">{user?.name || 'Coach'}</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button
            className="notif-btn"
            onClick={() => navigate('/reports')}
            title={t('reports.inbox_title')}
          >
            <Dumbbell size={20} />
            {notifCount > 0 && <span className="notif-badge">{notifCount > 9 ? '9+' : notifCount}</span>}
          </button>
          <button className="btn btn-sm btn-secondary" onClick={handleLogout} style={{ gap: '6px' }}>
            <LogOut size={14} />
            {t('coach_dashboard.logout')}
          </button>
        </div>
      </div>

      <div className="card mb-4" style={{
        background: 'linear-gradient(135deg, rgba(var(--primary-rgb),0.12) 0%, rgba(var(--primary-rgb),0.04) 100%)',
        border: '1px solid rgba(var(--primary-rgb),0.3)'
      }}>
        <p style={{ fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--muted-foreground)', marginBottom: '8px' }}>
          {t('coach_dashboard.invite_code_label')}
        </p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
          <span style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '28px',
            fontWeight: '800',
            color: 'var(--primary)',
            letterSpacing: '0.12em'
          }}>
            {user?.invite_code || '———'}
          </span>
          <button
            className="btn btn-sm btn-outline"
            onClick={handleCopyCode}
            style={{ gap: '6px', minWidth: '96px' }}
          >
            {copied
              ? <><Check size={14} /> {t('coach_dashboard.copied')}</>
              : <><Copy size={14} /> {t('coach_dashboard.copy')}</>
            }
          </button>
        </div>
        <p style={{ fontSize: '12px', color: 'var(--muted-foreground)', marginTop: '8px' }}>
          {t('coach_dashboard.invite_desc')}
        </p>
      </div>

      <div className="stats-grid mb-4">
        <div className="stat-card">
          <div className="stat-value">{clients.length}</div>
          <div className="stat-label">{t('coach_dashboard.stat_athletes')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{clients.reduce((acc, c) => acc + (c.total_sessions || 0), 0)}</div>
          <div className="stat-label">{t('coach_dashboard.stat_sessions')}</div>
        </div>
      </div>

      <h3 style={{ fontSize: '16px', marginBottom: '12px', color: 'var(--foreground)', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Users size={16} style={{ color: 'var(--primary)' }} />
        {t('coach_dashboard.athletes_title')}
      </h3>

      {clients.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🏋️</div>
          <h3 className="empty-title">{t('coach_dashboard.no_athletes_title')}</h3>
          <p className="empty-desc">
            {t('coach_dashboard.no_athletes_desc', { code: user?.invite_code })}
          </p>
        </div>
      ) : (
        clients.map(client => (
          <div
            key={client.user_id}
            className="card mb-3"
            style={{ cursor: 'pointer' }}
            onClick={() => navigate(`/coach/athlete/${client.user_id}`)}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '44px', height: '44px', borderRadius: '50%',
                  background: 'rgba(var(--primary-rgb),0.15)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '18px', flexShrink: 0
                }}>
                  {client.picture
                    ? <img src={client.picture} alt="" style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover' }} />
                    : '🐉'
                  }
                </div>
                <div>
                  <p style={{ fontWeight: '600', color: 'var(--foreground)', marginBottom: '2px' }}>{client.name}</p>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--muted-foreground)', display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <Dumbbell size={11} /> {t('coach_dashboard.routines_count', { count: client.total_workouts || 0 })}
                    </span>
                    <span style={{ fontSize: '12px', color: 'var(--muted-foreground)', display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <Calendar size={11} /> {t('coach_dashboard.sessions_count', { count: client.total_sessions || 0 })}
                    </span>
                  </div>
                  {client.last_session_date && (
                    <p style={{ fontSize: '11px', color: 'var(--muted-foreground)', marginTop: '2px' }}>
                      {t('coach_dashboard.last_session', { date: client.last_session_date })}
                    </p>
                  )}
                </div>
              </div>
              <ChevronRight size={18} style={{ color: 'var(--muted-foreground)', flexShrink: 0 }} />
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default CoachDashboard;
